import React, { useEffect, useState } from "react";
import SimpleLabelValue from "../SimpleLabelValue";
import httpService from "../../common/httpService";
import toast from "react-hot-toast";
import "./style.scss";

import {
  Box,
  Card,
  CardContent,
  Drawer,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import { DataGrid } from "@mui/x-data-grid";
import { CloseRounded } from "@mui/icons-material";
import { backendUrlImages } from "../../constant/config";

const SponsorsComponent = () => {
  const [sponsorsData, setSponsorsData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMsg, setErrorMsg] = useState("");
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [conferences, setConferences] = useState([]);
  const [conferenceId, setConferenceId] = useState(null);

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const TOKEN = localStorage.getItem("token");

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const openMenu = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const DEFAULT_ERROR_MESSAGE = "Failed to fetch sponsors.";

  // ---------------------------
  // Sponsorship helpers
  // ---------------------------
  const [sponsorships, setSponsorships] = useState([]);

  const fetchSponsorships = async () => {
    const getAuthToken = () => localStorage.getItem("token");
    const BaseUrl = process.env.REACT_APP_BASE_URL;

    try {
      await httpService({
        method: "GET",
        url: `${BaseUrl}/package/table/admin`,
        headers: { Authorization: `Bearer ${getAuthToken()}` },
        showLoader: true,
        onSuccess: (data) => {
          if (data?.sponsorships) setSponsorships(data.sponsorships);
        },
      });
    } catch (error) {}
  };

  useEffect(() => {
    fetchSponsorships();
  }, []);

  const getSponsorshipById = (id) => {
    const s = sponsorships.find((x) => x.id === id);
    return s ? s.item : "N/A";
  };

  const getSponsorshipByIdprice = (id) => {
    const s = sponsorships.find((x) => x.id === id);
    return s ? s.price : "N/A";
  };

  // ---------------------------
  // Utils (safe)
  // ---------------------------
  const safeText = (v) =>
    v === null || v === undefined || v === "" ? "N/A" : String(v);

  const safeJsonArray = (value) => {
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const safeDateTime = (v) => {
    try {
      if (!v) return "N/A";
      return new Date(v).toLocaleString("en-IN");
    } catch {
      return "N/A";
    }
  };

  // ---------------------------
  // PDF (fixed)
  // ---------------------------
  const downloadInvoicePdf = async (invoice) => {
    try {
      if (!invoice?.id) {
        toast.error("Invoice not found.");
        return;
      }
      if (!selectedSponsor) {
        toast.error("Sponsor not found.");
        return;
      }

      // Dynamic import => avoids bundler/SSR related failures [web:2]
      const [{ jsPDF }] = await Promise.all([import("jspdf")]); // [web:1]
      // html2canvas optional import (if later you want to embed screenshot/logo)
      // const html2canvas = (await import("html2canvas")).default;

      const pdf = new jsPDF("p", "mm", "a4"); // A4 portrait [web:1]
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const marginX = 20;
      const rightX = pageWidth - marginX;
      let y = 18;

      const ensurePage = (space = 10) => {
        if (y + space > pageHeight - 15) {
          pdf.addPage();
          y = 18;
        }
      };

      const drawSectionTitle = (title, color = [155, 19, 33]) => {
        ensurePage(14);
        pdf.setFillColor(color[0], color[1], color[2]);
        pdf.rect(marginX, y, pageWidth - marginX * 2, 8, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(10);
        pdf.text(title, marginX + 2, y + 6);
        y += 12;
      };

      const drawKV = (label, value) => {
        ensurePage(10);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(40, 40, 40);

        const labelText = `${label}:`;
        pdf.text(labelText, marginX, y);

        // Wrap long values safely [web:1]
        const valueMaxWidth = pageWidth - marginX * 2 - 45;
        const lines = pdf.splitTextToSize(safeText(value), valueMaxWidth); // [web:1]
        pdf.text(lines, marginX + 45, y);

        // move y according to lines
        y += Math.max(6, lines.length * 5);
      };

      const drawTwoColRow = (left, right) => {
        ensurePage(10);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(40, 40, 40);

        const leftLines = pdf.splitTextToSize(safeText(left), (pageWidth - marginX * 2) / 2 - 2);
        const rightLines = pdf.splitTextToSize(safeText(right), (pageWidth - marginX * 2) / 2 - 2);

        pdf.text(leftLines, marginX, y);
        pdf.text(rightLines, marginX + (pageWidth - marginX * 2) / 2, y);

        y += Math.max(leftLines.length, rightLines.length) * 5 + 2;
      };

      // ========== HEADER ==========
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.setTextColor(155, 19, 33);
      pdf.text("INVOICE", marginX, y);
      y += 10;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(90, 90, 90);
      pdf.text(`Invoice ID: ${safeText(invoice.id)}`, marginX, y);
      pdf.text(`Created: ${safeDateTime(invoice.created_at)}`, rightX, y, { align: "right" });
      y += 10;

      // ========== SPONSOR DETAILS ==========
      drawSectionTitle("SPONSOR DETAILS", [155, 19, 33]);

      drawTwoColRow(`Company Name: ${safeText(selectedSponsor.company_name)}`, `Status: ${safeText(selectedSponsor.status)}`);
      drawTwoColRow(`Contact Person: ${safeText(selectedSponsor.contact_person)}`, `Registration Type: ${safeText(selectedSponsor?.user?.registration_type)}`);
      drawTwoColRow(`Email: ${safeText(selectedSponsor?.user?.email)}`, `Phone: ${safeText(selectedSponsor?.user?.phone_number)}`);
      drawTwoColRow(`WhatsApp: ${safeText(selectedSponsor?.user?.whatsapp_number)}`, `Address: ${safeText(selectedSponsor.company_address)}`);

      // Files/links (PDF me text as URL)
      const firstAdUrl = selectedSponsor?.first_advertisement
        ? `${backendUrlImages}${selectedSponsor.first_advertisement}`
        : "N/A";
      const secondAdUrl = selectedSponsor?.second_advertisement
        ? `${backendUrlImages}${selectedSponsor.second_advertisement}`
        : "N/A";
      const logoUrl = selectedSponsor?.logo ? `${backendUrlImages}${selectedSponsor.logo}` : "N/A";
      const signUrl = selectedSponsor?.contract_signature
        ? `${backendUrlImages}${selectedSponsor.contract_signature}`
        : "N/A";

      drawKV("First Advertisement", firstAdUrl);
      drawKV("Second Advertisement", secondAdUrl);
      drawKV("Logo", logoUrl);
      drawKV("Contract Signature", signUrl);

      // ========== INVOICE DETAILS ==========
      drawSectionTitle("INVOICE DETAILS", [27, 94, 32]);

      drawTwoColRow(`Invoice ID: ${safeText(invoice.id)}`, `User ID: ${safeText(invoice.user_id)}`);
      drawTwoColRow(`Total Amount: $${safeText(invoice.total_amount)}`, `Additional Shell Scheme Cost: ${invoice.additional_cost_for_shell_scheme_booth ? "Yes" : "No"}`);
      drawTwoColRow(`Created At: ${safeDateTime(invoice.created_at)}`, `Updated At: ${safeDateTime(invoice.updated_at)}`);

      // optional fields (agar aate hain)
      if (invoice.square_meters || invoice.exhibit_number || invoice.shell_scheme_price) {
        drawTwoColRow(`Square Meters: ${safeText(invoice.square_meters)}`, `Exhibit Number: ${safeText(invoice.exhibit_number)}`);
        drawTwoColRow(
          `Shell Scheme Price: ${invoice.shell_scheme_price ? `$${invoice.shell_scheme_price}` : "N/A"}`,
          ""
        );
      }

      // ========== SPONSORSHIP OPTIONS ==========
      drawSectionTitle("SPONSORSHIP OPTIONS", [155, 19, 33]);

      const optionIds = safeJsonArray(invoice?.conference_sponsorship_option_ids);
      if (optionIds.length === 0) {
        drawKV("Options", "N/A");
      } else {
        optionIds.forEach((id, idx) => {
          ensurePage(10);
          const name = getSponsorshipById(id);
          const price = getSponsorshipByIdprice(id);
          drawTwoColRow(`Sponsorship Option ${idx + 1}: ${safeText(name)}`, `Price ${idx + 1}: ${safeText(price)}`);
        });
      }

      // ========== SPONSORSHIP OPTIONS (DETAILS) ==========
      const details = Array.isArray(invoice?.sponsorshipOptions) ? invoice.sponsorshipOptions : [];
      if (details.length) {
        drawSectionTitle("SPONSORSHIP OPTIONS (DETAILS)", [27, 94, 32]);

        details.forEach((opt, idx) => {
          ensurePage(16);
          drawKV(`Title ${idx + 1}`, safeText(opt?.title));
          drawKV(`Description ${idx + 1}`, safeText(opt?.description));
          drawKV(`Price ${idx + 1}`, opt?.price ? `$${opt.price}` : "N/A");
          y += 2;
        });
      }

      // ========== FOOTER (page numbers) ==========
      const pageCount = pdf.internal.getNumberOfPages(); // [web:1]
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(130, 130, 130);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 8, { align: "center" });
      }

      pdf.save(`invoice-${invoice.id}.pdf`); // triggers download [web:1]
      toast.success("Invoice downloaded!");
    } catch (e) {
      console.error("PDF download error:", e);
      toast.error("Failed to download PDF.");
    }
  };

  // ---------------------------
  // Data fetch
  // ---------------------------
  const fetchSponsors = async (page = 1) => {
    try {
      await httpService({
        method: "GET",
        url: `${BASE_URL}/sponsor/all`,
        params: { page },
        headers: { Authorization: `Bearer ${TOKEN}` },
        onSuccess: (data) => {
          setSponsorsData(data.sponsors || []);
          setTotalPages(data?.totalPages || 1);
          setCurrentPage(Number(data?.currentPage) || 1);
        },
        onError: (error) => {
          setErrorMsg(error?.message || DEFAULT_ERROR_MESSAGE);
          toast.error(error?.message || DEFAULT_ERROR_MESSAGE);
        },
        withToast: true,
      });
    } catch (error) {
      setErrorMsg(DEFAULT_ERROR_MESSAGE);
      toast.error(DEFAULT_ERROR_MESSAGE);
    }
  };

  const handlePageChange = (page) => fetchSponsors(page);

  const fetchConferences = async () => {
    try {
      await httpService({
        method: "GET",
        url: `${BASE_URL}/conferences/all`,
        onSuccess: (data) => setConferences(data.data),
        onError: (error) => setErrorMsg(error?.message || "Failed to fetch conferences."),
      });
    } catch (error) {
      setErrorMsg("Failed to fetch conferences.");
    }
  };

  useEffect(() => {
    fetchConferences();
  }, []);

  useEffect(() => {
    fetchSponsors();
  }, [conferenceId]);

  // ---------------------------
  // DataGrid
  // ---------------------------
  const rows = sponsorsData.map((sponsor) => ({
    ...sponsor,
    id: sponsor.id,
    company_name: sponsor.company_name,
    contact_person: sponsor.contact_person,
    status: sponsor.status,
    created_at: sponsor.created_at,
    actions: sponsor.actions,
  }));

  const columns = [
    { field: "id", headerName: "ID", flex: 1, minWidth: 230, cellClassName: "centered-cell" },
    { field: "company_name", headerName: "Company Name", flex: 1, minWidth: 230, cellClassName: "centered-cell" },
    { field: "contact_person", headerName: "Contact Person", flex: 1, minWidth: 230, cellClassName: "centered-cell" },
    { field: "status", headerName: "Status", flex: 1, minWidth: 230, cellClassName: "centered-cell" },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
      renderCell: (params) => <>{new Date(params.row.created_at).toLocaleString()}</>,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 230,
      cellClassName: "centered-cell",
      renderCell: (params) => (
        <>
          <IconButton onClick={(event) => openMenu(event, params.row)}>
            <MoreVertIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl) && selectedRow?.id === params.row.id}
            onClose={closeMenu}
          >
            <MenuItem
              onClick={() => {
                setSelectedSponsor(params.row);
                setDrawerOpen(true);
                closeMenu();
              }}
            >
              View Details
            </MenuItem>
          </Menu>
        </>
      ),
    },
  ];

  return (
    <div style={{ borderRadius: "8px", width: "100%", maxWidth: "1700px", padding: "20px" }}>
      <div className="sponsors-component">
        <Typography
          variant="h6"
          sx={{
            color: "#c62828",
            fontWeight: "bold",
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            textAlign: "center",
          }}
        >
          All Sponsors
        </Typography>

        <DataGrid
          getRowId={(row) => row.id}
          rows={rows}
          columns={columns}
          paginationModel={{ page: currentPage - 1, pageSize: 10 }}
          onPaginationModelChange={(pagination) => {
            setCurrentPage(pagination.page + 1);
            handlePageChange(pagination.page + 1);
          }}
          rowCount={totalPages * 10}
          pageSizeOptions={[10]}
          paginationMode="server"
          checkboxSelection
          disableRowSelectionOnClick
          autoHeight
          sx={{
            marginTop: "20px",
            "& .MuiDataGrid-virtualScroller": { overflow: "hidden" },
          }}
        />

        <Drawer
          open={isDrawerOpen}
          onClose={() => setDrawerOpen(false)}
          anchor="right"
          sx={{
            zIndex: (theme) => theme.zIndex.modal + 1,
            "& .MuiDrawer-paper": {
              zIndex: (theme) => theme.zIndex.modal + 1,
              width: { xs: "100%", sm: "50%", md: "50%", lg: "40%", xl: "40%" },
            },
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-end", padding: 2 }}>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseRounded />
            </IconButton>
          </div>

          <Typography
            variant="h6"
            gutterBottom
            sx={{
              color: "#9B1321",
              textAlign: "center",
              backgroundColor: "#f1f1f1",
              padding: 1,
              borderRadius: 1,
              marginBottom: 2,
            }}
          >
            Sponsor Details
          </Typography>

          <Box sx={{ padding: 2, overflowY: "auto" }}>
            {selectedSponsor ? (
              <Card sx={{ marginBottom: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <SimpleLabelValue label="Company Name" value={selectedSponsor.company_name || "-"} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SimpleLabelValue label="Contact Person" value={selectedSponsor.contact_person || "-"} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SimpleLabelValue label="Email" value={selectedSponsor?.user?.email || "-"} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SimpleLabelValue label="Phone Number" value={selectedSponsor?.user?.phone_number || "-"} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SimpleLabelValue label="WhatsApp Number" value={selectedSponsor?.user?.whatsapp_number || "-"} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SimpleLabelValue label="Address" value={selectedSponsor.company_address || "-"} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SimpleLabelValue label="Status" value={selectedSponsor.status || "-"} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <SimpleLabelValue label="Registration Type" value={selectedSponsor?.user?.registration_type || "-"} />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        color="primary"
                        onClick={() =>
                          selectedSponsor.first_advertisement &&
                          window.open(`${backendUrlImages}${selectedSponsor.first_advertisement}`, "_blank")
                        }
                        style={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        {selectedSponsor.first_advertisement ? "First Advertisement" : "-"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        color="primary"
                        onClick={() =>
                          selectedSponsor.second_advertisement &&
                          window.open(`${backendUrlImages}${selectedSponsor.second_advertisement}`, "_blank")
                        }
                        style={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        {selectedSponsor.second_advertisement ? "Second Advertisement" : "-"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        color="primary"
                        onClick={() => selectedSponsor.logo && window.open(`${backendUrlImages}${selectedSponsor.logo}`, "_blank")}
                        style={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        {selectedSponsor.logo ? "Logo" : "-"}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography
                        variant="body2"
                        color="primary"
                        onClick={() =>
                          selectedSponsor.contract_signature &&
                          window.open(`${backendUrlImages}${selectedSponsor.contract_signature}`, "_blank")
                        }
                        style={{ cursor: "pointer", textDecoration: "underline" }}
                      >
                        {selectedSponsor.contract_signature ? "Contract Signature" : "-"}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            ) : (
              <Typography variant="body1" color="text.secondary">
                No sponsor details available.
              </Typography>
            )}

            <Typography variant="h5" component="div" gutterBottom color="#9B1321">
              Invoices
            </Typography>

            {selectedSponsor?.sponsor_invoices?.length > 0 ? (
              selectedSponsor.sponsor_invoices.map((invoice, index) => (
                <Card key={index} sx={{ marginBottom: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      {invoice?.id && (
                        <Grid item xs={12} md={6}>
                          <SimpleLabelValue label="Invoice ID" value={invoice.id} />
                        </Grid>
                      )}

                      {invoice?.user_id && (
                        <Grid item xs={12} md={6}>
                          <SimpleLabelValue label="User ID" value={invoice.user_id} />
                        </Grid>
                      )}

                      {invoice?.total_amount && (
                        <Grid item xs={12} md={6}>
                          <SimpleLabelValue label="Total Amount" value={`$${invoice.total_amount}`} />
                        </Grid>
                      )}

                      {invoice?.created_at && (
                        <Grid item xs={12} md={6}>
                          <SimpleLabelValue label="Created At" value={new Date(invoice.created_at).toLocaleString("en-IN")} />
                        </Grid>
                      )}

                      {invoice?.updated_at && (
                        <Grid item xs={12} md={6}>
                          <SimpleLabelValue label="Updated At" value={new Date(invoice.updated_at).toLocaleString("en-IN")} />
                        </Grid>
                      )}

                      {invoice?.additional_cost_for_shell_scheme_booth !== undefined && (
                        <Grid item xs={12} md={6}>
                          <SimpleLabelValue
                            label="Additional Cost for Shell Scheme Booth"
                            value={invoice.additional_cost_for_shell_scheme_booth ? "Yes" : "No"}
                          />
                        </Grid>
                      )}

                      {/* Sponsorship Options */}
                      {invoice?.conference_sponsorship_option_ids && (
                        <Grid item xs={12}>
                          <Typography variant="h6">Sponsorship Options</Typography>

                          {safeJsonArray(invoice.conference_sponsorship_option_ids).map((id, idx) => (
                            <div key={idx}>
                              <SimpleLabelValue label={`Sponsorship Option ${idx + 1}`} value={getSponsorshipById(id)} />
                              <SimpleLabelValue label={`Price ${idx + 1}`} value={getSponsorshipByIdprice(id)} />
                            </div>
                          ))}
                        </Grid>
                      )}

                      {invoice?.sponsorshipOptions?.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="h6">Sponsorship Options (Details)</Typography>
                          {invoice.sponsorshipOptions.map((option, idx) => (
                            <div key={idx}>
                              <SimpleLabelValue label="Title" value={option?.title || "-"} />
                              <SimpleLabelValue label="Description" value={option?.description || "-"} />
                              <SimpleLabelValue label="Price" value={option?.price ? `$${option.price}` : "-"} />
                            </div>
                          ))}
                        </Grid>
                      )}

                      {/* Download Button */}
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                          <button
                            type="button"
                            onClick={() => downloadInvoicePdf(invoice)}
                            style={{
                              padding: "10px 16px",
                              borderRadius: "6px",
                              border: "none",
                              background: "#9B1321",
                              color: "#fff",
                              cursor: "pointer",
                              fontWeight: "bold",
                              fontSize: "14px",
                            }}
                          >
                            Download Invoice
                          </button>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography variant="body1" color="text.secondary">
                No invoices available for this sponsor.
              </Typography>
            )}
          </Box>
        </Drawer>
      </div>
    </div>
  );
};

export default SponsorsComponent;
